
import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { addClass, removeClass, find, getParent } from '@fretadao/f-js-dom';
import { isPresent, isBlank, sortBy, groupBy, hoursToMinutes, seconds } from '@fretadao/f-js-utils';
import { API } from '../../../src/api';

import L from 'leaflet';
import 'leaflet.gridlayer.googlemutant';
import 'leaflet-providers/leaflet-providers';
import 'leaflet.markercluster/dist/leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'packs/leaflet.forceZIndex';
import 'leaflet-sidebar-v2/js/leaflet-sidebar';
import 'leaflet-easybutton';
import 'leaflet-extra-markers';
import 'leaflet-contextmenu';
import 'src/vendor/leaflet-playback';

application.register('admin-travel-plan-map', class extends Controller {
  static targets = [
    'mapContainer',
    'routeInfoBox',
    'spinnerOverlay',
    'updateWaypointFormModal',
    'updateWaypointFormModalUUID',
    'updateWaypointFormModalLatitude',
    'updateWaypointFormModalLongitude',
    'updateWaypointFormModalIndex',
    'updateWaypointFormModalOfficialSchedule',
    'updateWaypointFormModalReference',
    'updateWaypointFormModalCustomRadiusInMeters',
    'updateWaypointFormModalKindPoint',
    'deleteWaypointFormModal',
    'deleteWaypointFormModalUUID',
    'addWaypointFormModal',
    'addWaypointFormModalUUID',
    'addWaypointFormModalLatitude',
    'addWaypointFormModalLongitude',
    'addWaypointFormModalIndex',
    'addWaypointFormModalOfficialSchedule',
    'addWaypointFormModalReference',
    'addWaypointFormModalCustomRadiusInMeters',
    'addWaypointFormModalKindPoint',
    'addWaypointSideBarLatitude',
    'addWaypointSidebarLongitude',
    'addWaypointSidebarIndex',
    'addWaypointSidebarOfficialSchedule',
    'addWaypointSidebarStreetLine',
    'addWaypointSidebarReference',
    'addWaypointSidebarCustomRadiusInMeters',
    'addWaypointSidebarKindPoint',
    'historyInputContainer',
    'historyContentContainer',
    'applyHistoryBtn',
    'cleanHistoryBtn'
  ];

  static values = {
    editable: { type: Boolean, default: true },
    lineId: String,
    travelPlanId: String,
    showHistory: { type: Boolean, default: false },
    vehicleId: { type: Number, default: 0 }
  };

  UUIDV4 = require('uuid/v4');
  INIT_COORDINATES = L.latLng(-23.6821604, -46.8754915);
  INIT_ZOOM = 14;
  MIN_ZOOM = 6;
  MAX_ZOOM = 24;
  MAX_ZINDEX = 9999;
  MAX_MINUS_ONE_ZINDEX = 9998;

  map = null;

  connect() {
    this.#startLoading();
    this.lastRoutingResponse = {};
    this.currentRoute = null;
    this.routeLayers = {};
    this.controlLayers = {};
    this.contextMenuItems = {};
    this.#configMap();
    this.#garagesControlLayer();
    this.#subscriptionsControlLayer();
    this.#setHistoryMap();

    API.admin.travelPlan
      .fetch(this.lineIdValue, this.travelPlanIdValue)
      .then(this.#loadTravelPlan.bind(this))
      .then(this.#stopLoading.bind(this))
      .catch((error) => {
        console.error(error);
        alert(`Não foi possível carregar os dados do plano de viagem. ${error.message}`);
      });
  }

  disconnect() {
    this.map.eachLayer((layer) => layer.remove());
    this.map.remove();
    this.map = null;
  }

  #setHistoryMap() {
    if (this.hasShowHistoryValue && this.showHistoryValue) {
      this.inputsContainer = this.historyInputContainerTarget;
      this.contentContainer = this.historyContentContainerTarget;
      this.applyButton = this.applyHistoryBtnTarget;
      this.cleanButton = this.cleanHistoryBtnTarget;
      this.vehicleId = this.vehicleIdValue;
      this.markerClusterGroups = [];
    }
  }

  loadLineLocationsHistory() {
    gtag('event', 'line_map_load_line_locations_history');

    const initialDate = find('#initial_date', this.historyInputContainerTarget)?.value;
    const initialTime = find('#initial_time', this.historyInputContainerTarget)?.value;

    const endDate = find('#end_date', this.historyInputContainerTarget).value;
    const endTime = find('#end_time', this.historyInputContainerTarget).value;

    if (!initialDate || !endDate) {
      this.historyContentContainerTarget.innerHTML = `
        <span class="Txt--red">Preencha todos os campos de data e hora.</span>
      `;
      return;
    }

    const fromDatetime = moment(`${initialDate} ${initialTime}`, 'YYYY-MM-DD HH:mm').format();
    const toDatetime = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD HH:mm').format();

    this.historyCleanUp();
    this.historyContentContainerTarget.innerHTML = 'Carregando...';

    this.#fetchLineLocationsHistory(fromDatetime, toDatetime)
      .then((result) => this.#addLocationHistoryInputs(result))
      .catch((error) => {
        console.error(error);
        this.historyContentContainerTarget.innerHTML = 'Não há histórico';
        this.#setCleanButton();
      }).finally(() => this.#setCleanButton());
  }

  #setCleanButton() {
    removeClass(this.cleanButton, 'disabled');
    this.cleanButton.disabled = false;
  }

  historyCleanUp() {
    this.historyContentContainerTarget.innerHTML = '';
    addClass(this.cleanButton, 'disabled');
    this.cleanButton.disabled = true;

    if (this.vehicleHistoryPlayback) this.vehicleHistoryPlayback.destroy();
    if (this.markerClusterGroups.length > 0) {
      this.markerClusterGroups.forEach((cluster) => this.map.removeLayer(cluster));
    }

    this.markerClusterGroups = [];
    this.vehicleHistoryPlayback = null;
  }

  #setPlayback(playbackTrack) {
    const playbackOptions = {
      tracksLayer: true,
      playControl: true,
      dateControl: true,
      sliderControl: true,
      popups: true,
      marker: (featureData) => {
        return {
          icon: L.ExtraMarkers.icon({
            svg: true,
            icon: 'fa-bus',
            markerColor: 'violet',
            shape: 'star',
            prefix: 'fa'
          }),
          getTooltip: (featureData) => {
            return featureData.properties.vehicleName;
          },
        }
      }
    }

    if (this.vehicleHistoryPlayback) this.vehicleHistoryPlayback.destroy();

    this.vehicleHistoryPlayback = new L.Playback(
      this.map,
      playbackTrack,
      null,
      playbackOptions
    );
  }

  #fetchLineLocationsHistory(fromDatetime, toDatetime) {
    return new Promise((resolve, reject) => {
      $.get(Routes.admin_line_locations_history_index_path(this.lineIdValue, {
        from_datetime: fromDatetime,
        to_datetime: toDatetime,
        direction: this.direction
      }), (data) => { return resolve(data) })
        .catch(error => { return reject(error) });
    });
  }

  #addLocationHistoryInputs(results) {
    this.historyContentContainerTarget.innerHTML = '';

    let tracks = [];

    results.forEach((result, index) => {
      Object.entries(result).forEach((entry) => {
        this.#appendLocationHistoryInput(index, entry);
      });
    });

    $('input[name=history]').on('change', (event) => {
      const target = $(event.target);
      const targetDate = target.data('date');
      const targetIndex = target.data('index');
      const targetDirection = target.data('direction');
      const entry = results[targetIndex][targetDate][targetDirection];

      if (target.is(':checked')) {
        this.map.addLayer(entry.markerClusterGroup);
        this.#setPlayback(entry.playbackTrack);
      } else {
        this.map.removeLayer(entry.markerClusterGroup);
        if (this.vehicleHistoryPlayback) this.vehicleHistoryPlayback.destroy();
      }
    });
  }

  #appendLocationHistoryInput(index, entry) {
    const date = entry[0];
    const locationData = entry[1];
    let inputs = '';

    if (!locationData) {
      inputs += '<label style="vertical-align: super; line-height:normal;">Não há escalas</label>';
    } else if (locationData.roundtrip) {
      let label = `Ida & Volta: ${locationData.roundtrip.vehicle_name}`

      inputs += '<div>';
      if (locationData.roundtrip.history.length > 0) {
        inputs += `<input type="checkbox" name="history" data-index=${index} data-date=${date} data-direction='roundtrip' class='Txt--sm' />`;
        this.#buildClusters(date, locationData.roundtrip);
      } else {
        label = '<p>Não há histórico</p>' + label;
      }

      inputs += `<label style="vertical-align: super; line-height:normal;">${label}</label>`;
      inputs += '</div>';
    } else {
      if (locationData.ingoing) {
        let label = `Ida: ${locationData.ingoing.vehicle_name}`

        inputs += '<div>';
        if (locationData.ingoing.history.length > 0) {
          inputs += `<input type="checkbox" name="history" data-index=${index} data-date=${date} data-direction='ingoing' class='Txt--sm' />`;
          this.#buildClusters(date, locationData.ingoing);
        } else {
          label = '<p>Não há histórico</p>' + label;
        }

        inputs += `<label style="vertical-align: super;">${label}</label>`;
        inputs += '</div>';
      }

      if (locationData.outgoing) {
        let label = `Volta: ${locationData.outgoing.vehicle_name}`

        inputs += '<div>';
        if (locationData.outgoing.history.length > 0) {
          inputs += `<input type="checkbox" name="history" data-index=${index} data-date=${date} data-direction='outgoing' class='Txt--sm' />`;
          this.#buildClusters(date, locationData.outgoing);
        } else {
          label = '<p>Não há histórico</p>' + label;
        }

        inputs += `<label style="vertical-align: super;">${label}</label>`;
        inputs += '</div>';
      }
    }

    const dateDisplay = moment(date).locale('pt-br');

    $(
      [
        `<p class="Txt--bold Txt--base">${dateDisplay.format('DD/MM/YYYY')} - ${dateDisplay.format('dddd')}</p>`,
        '<p class="Txt--sm">',
        `  ${inputs}`,
        '</p>',
      ].join('\n')
    ).appendTo(this.historyContentContainerTarget);
  }

  #buildClusters(date, locationData) {
    let coordinates = [];
    let timestamps = [];
    let speeds = [];

    const markerClusterGroup = L.markerClusterGroup({
      singleMarkerMode: true,
      maxClusterRadius: 25,
    });

    const locationHistoryData = locationData.history;

    locationHistoryData.forEach((locationHistory) => {
      let popup = '<p>Id: ' + locationHistory.id + '</p>';
      popup += `<p>Rastreador: ${locationHistory.source || 'Não registrado'}</p>`;
      popup += '<p>Horário: ' + locationHistory.registered_at.substr(11, 8) + '</p>';
      popup += '<p>Registrado em: ' + locationHistory.registered_at + '</p>';
      popup += '<p>Velocidade: ' + locationHistory.speed + '</p>';
      popup += '<p>ID do veículo: ' + locationHistory.vehicle_id + '</p>';
      popup += '<p>Lat,Lng: ' + locationHistory.latitude + ',' + locationHistory.longitude + '</p>';

      const icon = L.ExtraMarkers.icon({
        icon: 'fa-circle',
        markerColor: 'black',
        prefix: 'fa',
        svg: true,
        shape: 'circle'
      });
      const marker = L.marker([locationHistory.latitude, locationHistory.longitude], {
        draggable: false,
        alt: locationHistory,
        icon: icon,
        popup: popup, // copy of the poput text will be
      });

      marker.bindPopup(popup);

      coordinates.push([locationHistory.longitude, locationHistory.latitude]);
      timestamps.push(Date.parse(locationHistory.registered_at));
      speeds.push(locationHistory.speed);

      markerClusterGroup.addLayer(marker);
    });

    locationData.markerClusterGroup = markerClusterGroup;
    this.markerClusterGroups.push(markerClusterGroup);

    locationData.playbackTrack = {
      type: 'Feature',
      geometry: {
        type: 'MultiPoint',
        coordinates: coordinates
      },
      properties: {
        time: timestamps,
        speed: speeds,
        vehicleName: locationData.vehicle_name,
        date: date
      }
    }
  }

  #startLoading() {
    this.spinnerOverlayTarget.style.top = 0;
    this.spinnerOverlayTarget.style.left = 0;
    this.spinnerOverlayTarget.style.right = 0;
    this.spinnerOverlayTarget.style.bottom = 0;
    this.spinnerOverlayTarget.style.width = '100%';
    this.spinnerOverlayTarget.style.height = '100%';
    this.spinnerOverlayTarget.style.display = 'flex';
    this.spinnerOverlayTarget.style.position = 'fixed';
    this.spinnerOverlayTarget.style.backgroundColor = '#ffffff80';
    this.spinnerOverlayTarget.style.zIndex = this.MAX_ZINDEX * 100;
  }

  #stopLoading() {
    this.spinnerOverlayTarget.style.cssText = 'display:none';
  }

  #loadTravelPlan(travelPlan, showDirection = null, firstLoading = true) {
    this.travelPlan = travelPlan;
    this.#plotRoutes({ travelPlan: this.travelPlan, showDirection: showDirection, fitMap: firstLoading });
    if (firstLoading) this.#addRouteButtons(this.travelPlan);
  }

  #routesFor({ travelPlan }) {
    return [travelPlan.ingoingRoute, travelPlan.outgoingRoute].filter(isPresent);
  }

  #deleteWaypointsWithPassengersAlert() {
    alert('Este ponto possui passageiros vinculados e não pode ser excluido.');
  }

  #setRoutingData(routingData) {
    this.lastRoutingResponse = routingData;
    return routingData;
  }

  #buildRouteFrom({ coordinates, engine, vehicleCharacteristics }) {
    const body = {
      coordinates: coordinates,
      engine: engine,
      vehicleCharacteristics: vehicleCharacteristics
    }
    return API.maps.createRouting(this.#csrfToken(), body)
    .then(this.#setRoutingData.bind(this));
  }

  #createMarker({ at, iconUrl, icon, popupText, tooltipText, draggable = false }) {
    if (isPresent(iconUrl)) icon = L.icon({ iconUrl: iconUrl, zIndex: 11000, iconSize: [32, 40], iconAnchor: [16, 40] });
    const marker = L.marker(at, { icon: icon, draggable: draggable });

    if (isPresent(popupText)) marker.bindPopup(popupText);
    if (isPresent(tooltipText)) marker.bindTooltip(tooltipText, { permanent: true, direction: 'auto', });

    return marker;
  }

  #createWaypointMarkers({ route, draggable = false }) {
    return route.waypoints
      .map((waypointData) => this.#addWaypointMarker(waypointData, route, draggable));
  }

  #waypointIconColor(waypointData, direction) {
    if (waypointData.kindPoint === 'fake_stop') return '#ca842b';
    if (direction === 'ingoing') return '#27ca36';
    return '#ca2742';
  }

  #waypointShape(waypointData) {
    return waypointData.kindPoint === 'stop' ? 'circle' : 'penta';
  }

  #addWaypointMarker(waypointData, route, draggable) {
    const iconColor = this.#waypointIconColor(waypointData, route.direction);
    const iconShape = this.#waypointShape(waypointData);
    const icon = L.ExtraMarkers.icon({ icon: 'fa-circle', markerColor: iconColor, prefix: 'fa', svg: true, shape: iconShape });
    const waypointMarker = this.#createMarker(
      {
        at: waypointData.coords,
        icon: icon,
        draggable: draggable,
        popupText: this.#waypointPopupText(waypointData),
        tooltipText: this.#waypointTooltipText(waypointData),
      }
    );

    if (!this.#isEditable()) return waypointMarker;

    waypointMarker._routeId = route.id;
    waypointMarker._waypointData = waypointData;

    const openUpdateWaypointModal = (marker) => {
      const coordinates = marker._latlng;
      const waypoint = marker._waypointData;
      const waypointFormModal = $(this.updateWaypointFormModalTarget);

      this.updateWaypointFormModalUUIDTarget.value = waypoint.uuid;
      this.updateWaypointFormModalLatitudeTarget.value = coordinates.lat;
      this.updateWaypointFormModalLongitudeTarget.value = coordinates.lng;
      this.updateWaypointFormModalIndexTarget.value = waypoint.index;
      this.updateWaypointFormModalOfficialScheduleTarget.value = waypoint.officialSchedule;
      this.updateWaypointFormModalReferenceTarget.value = waypoint.reference;
      this.updateWaypointFormModalCustomRadiusInMetersTarget.value = waypoint.customRadiusInMeters;

      const virtualStopOption = find('option[value=fake_stop]', this.updateWaypointFormModalKindPointTarget);
      const optionToSelect = find(`option[value=${waypoint.kindPoint}]`, this.updateWaypointFormModalKindPointTarget);

      virtualStopOption.disabled = waypoint.kindPoint === 'stop' ? true : false;
      optionToSelect.selected = true;

      waypointFormModal.modal({
        appendTo: '#modal-content',
        close: true,
        overlayClose: true,
        zIndex: this.MAX_MINUS_ONE_ZINDEX,
        onClose: () => {
          const route = this.#routesFor({ travelPlan: this.travelPlan }).find((route) => route.id === marker._routeId);
          const draggableWaypoint = this.#isEditable();

          this.#plotRoute({ route: route, draggableWaypoint: draggableWaypoint, show: true, fitMap: false })
          this.lastRoutingResponse = {};
          $.modal.close();
        },
      });
    };

    const openDeleteWaypointModal = (marker) => {
      const waypoint = marker._waypointData;
      if (waypoint.hasPassengers) return this.#deleteWaypointsWithPassengersAlert();

      const waypointFormModal = $(this.deleteWaypointFormModalTarget);
      this.deleteWaypointFormModalUUIDTarget.value = waypoint.uuid;

      waypointFormModal.modal({
        appendTo: '#modal-content',
        close: true,
        overlayClose: true,
        zIndex: this.MAX_MINUS_ONE_ZINDEX
      });
    };

    waypointMarker.bindContextMenu({
      contextmenuInheritItems: false,
      contextmenuItems: [{
        text: "Atualizar Ponto",
        index: 0,
        callback: () => openUpdateWaypointModal(waypointMarker),
      },
      {
        separator: true,
        index: 1,
      },
      {
        text: "Remover Ponto",
        index: 2,
        callback: () => openDeleteWaypointModal(waypointMarker),
      }
      ]
    });

    const debounce = (func, wait) => {
      let timeout;

      return (...args) => {
        clearTimeout(timeout); // Cancel previous timer
        timeout = setTimeout(() => func(...args), wait); // configure new timer;
      };
    };

    const waitingTime = seconds(0.1);

    const openModalAfterDrag = debounce((dragEndEvent) => {
      if (this.isRebuildingPolyline) this.#startLoading();
      const marker = dragEndEvent.target;

      openUpdateWaypointModal(marker);
    },
      waitingTime
    );

    const rebuildPolyline = (moovingEvent) => {
      this.isRebuildingPolyline = true;
      const marker = moovingEvent.target;
      const movingWaypointData = marker._waypointData;
      const routeData = this.#routesFor({ travelPlan: this.travelPlan }).find((route) => route.id === marker._routeId);
      const coordinates = routeData
        .waypoints
        .map((waypointData) => waypointData.uuid === movingWaypointData.uuid ? [moovingEvent.latlng.lat, moovingEvent.latlng.lng] : waypointData.coords);
      const routingParams = {
        coordinates: coordinates,
        engine: this.currentRoute.routingEngine,
        vehicleCharacteristics: this.currentRoute.vehicleCharacteristics
      };

      this.#buildRouteFrom(routingParams)
        .then((routingData) => {
          const newPolyline = this.#buildPolyline(this.#decodePolyline(routingData.encodedPolyline), this.#routeColor(route));
          const layerGroup = this.routeLayers[this.#routeLayerIdentifier(route)];

          layerGroup.eachLayer((layer) => layer._groupName === 'Polylines' && layerGroup.removeLayer(layer));
          layerGroup.addLayer(newPolyline);
          this.#stopLoading();
          this.isRebuildingPolyline = false;
        })
        .catch((error) => {
          console.error(error);
          alert(`Não foi possível traçar a rota no mapa. ${error.message}`);
        });

    };

    const rebuildPolylineWhileDragging = debounce((moovingEvent) => rebuildPolyline(moovingEvent), waitingTime);


    waypointMarker.on('dragstart', (dragStartEvent) => {
      event.target._oldLatLng = isPresent(dragStartEvent.target._oldLatLng) ? dragStartEvent.target._oldLatLng : [];
      event.target._oldLatLng.push(dragStartEvent.target._latlng);
    });

    waypointMarker.on('drag', (event) => rebuildPolylineWhileDragging(event));

    waypointMarker.on('dragend', (dragEndEvent) => openModalAfterDrag(dragEndEvent));

    return waypointMarker
  }

  #generateUUID() {
    return this.UUIDV4();
  }
  #openAddWaypointModal(addWaypointEvent) {
    const latitude = addWaypointEvent.latlng.lat;
    const longitude = addWaypointEvent.latlng.lng;
    const waypointFormModal = $(this.addWaypointFormModalTarget);
    const stopOption = find('option[value=stop]', this.addWaypointFormModalKindPointTarget);

    this.addWaypointFormModalUUIDTarget.value = this.#generateUUID();
    this.addWaypointFormModalLatitudeTarget.value = latitude;
    this.addWaypointFormModalLongitudeTarget.value = longitude;
    this.addWaypointFormModalIndexTarget.value = '';
    this.addWaypointFormModalOfficialScheduleTarget.value = '';
    this.addWaypointFormModalReferenceTarget.value = '';
    this.addWaypointFormModalCustomRadiusInMetersTarget.value = this.addWaypointFormModalCustomRadiusInMetersTarget.dataset.defaultValue;

    stopOption.selected = true;

    waypointFormModal.modal({
      appendTo: '#modal-content',
      close: true,
      overlayClose: true,
      zIndex: this.MAX_MINUS_ONE_ZINDEX
    });
  };

  #isWaypointScheduleChanged(waypointData) {
    return waypointData.officialSchedule !== waypointData.schedule;
  }

  #waypointTooltipText(waypointData) {
    if (!this.#isWaypointScheduleChanged(waypointData)) return `Posição ${waypointData.index}: ${waypointData.officialSchedule}`;
    return `Posição ${waypointData.index}:<br/>Oficial: ${waypointData.officialSchedule} - Provisório: ${waypointData.schedule}`;
  }

  #waypointPopupText(waypointData) {
    if (waypointData.kindPoint === 'fake_stop') {
      return `<p>Ponto virtual - Posição: ${waypointData.index}</p>`;
    } else {
      const scheduleInfo = this.#isWaypointScheduleChanged(waypointData) ? ` / Provisório: ${waypointData.schedule}</p>` : '</p>';
      return `<p>Posição ${waypointData.index}:</p>` +
        `<p>Oficial: ${waypointData.officialSchedule}${scheduleInfo}<p>${waypointData.fullAddress}</p>`;
    }
  }

  #routeColor(route) {
    return route.direction === 'ingoing' ? 'green' : 'red';
  }

  #routeLayerIdentifier(route) {
    return `${route.id}-${route.direction}`;
  }

  #plotRoutes({ travelPlan, showDirection = null, fitMap = true }) {
    const draggableWaypoint = this.#isEditable();
    const routes = this.#routesFor({ travelPlan: travelPlan })
    const routeToShow = travelPlan[`${showDirection}Route`] || routes[0];

    routes
      .forEach(
        (route) => this.#plotRoute(
          {
            route: route,
            draggableWaypoint: draggableWaypoint,
            show: route.id === routeToShow.id,
            fitMap: fitMap
          }
        )
      );
  }

  #decodePolyline(encodedPolyline) {
    return L.PolylineUtil.decode(encodedPolyline);
  }

  #buildPolyline(decodedPolyline, color) {
    const outlineLine = L.polyline(decodedPolyline, {
      interactive: false,
      smoothFactor: 1.5,
      color: 'white',
      weight: 6,
      opacity: 1,
    });

    const routeLine = L.polyline(decodedPolyline, {
      interactive: false,
      smoothFactor: 1.5,
      color: color,
      weight: 3,
      opacity: 0.8,
    });

    const layerGroup = L.layerGroup([outlineLine, routeLine]);
    layerGroup._groupName = 'Polylines';

    return layerGroup;
  }

  #plotRoute({ route, draggableWaypoint, show = false, fitMap = true }) {
    if (!this.#routePlotable(route)) return;
    this.#removeRoute(route);

    const decodedPolyline = this.#decodePolyline(route.encodedPolyline);
    const polyline = this.#buildPolyline(decodedPolyline, this.#routeColor(route));
    const waypointMarkers = this.#createWaypointMarkers({ route: route, draggable: draggableWaypoint });
    const layerGroup = L.layerGroup([...waypointMarkers, polyline]);

    layerGroup._mapBounds = L.latLngBounds(decodedPolyline);
    this.routeLayers[this.#routeLayerIdentifier(route)] = layerGroup;
    this.#waypointsControllerLayer({ route: route });

    if (show) this.#showRoute(route, fitMap);
  }

  #routePlotable(route) {
    return isPresent(route) && route.waypoints.length >= 2;
  }

  #showRoute(route, fitMap = true) {
    const routeLayerGroup = this.routeLayers[this.#routeLayerIdentifier(route)];

    this.#loadRouteInfoBox(route);

    if (isBlank(routeLayerGroup)) return;
    if (fitMap) this.map.fitBounds(routeLayerGroup._mapBounds);

    this.map.addLayer(routeLayerGroup);
    this.currentRoute = route;
  }

  #hideRoute(route) {
    this.map.removeLayer(this.routeLayers[this.#routeLayerIdentifier(route)])
  }

  #removeRoute(route) {
    const routeLayerIdentifier = this.#routeLayerIdentifier(route);
    const waypointsControllerLayer = this.controlLayers[routeLayerIdentifier];

    this.routeLayers[routeLayerIdentifier]?.remove();

    if (isPresent(waypointsControllerLayer)) {
      waypointsControllerLayer.layerGroup.remove();
      this.map.removeLayer(waypointsControllerLayer.layerGroup);
      this.control.removeLayer(waypointsControllerLayer.layerGroup);
    }
  }

  #addRouteButtons(travelPlan) {
    if (isPresent(travelPlan.ingoingRoute)) {
      this.routeButtons.push({
        stateName: "ingoing",
        title: "Pontos de ida",
        icon: "fa-reply",
        onClick: (btn) => {
          btn.state("outgoing");

          if(isPresent(travelPlan.outgoingRoute)) this.#hideRoute(travelPlan.outgoingRoute);
          this.#showRoute(travelPlan.ingoingRoute);
        },
      })
    }

    if (isPresent(travelPlan.outgoingRoute)) {
      this.routeButtons.push({
        stateName: "outgoing",
        title: "Pontos de volta",
        icon: "fa-reply",
        onClick: (btn) => {
          btn.state("ingoing");

          if(isPresent(travelPlan.ingoingRoute)) this.#hideRoute(travelPlan.ingoingRoute);
          this.#showRoute(travelPlan.outgoingRoute);
        },
      })

      const button = this.#addStateButtons(this.routeButtons);
      isPresent(travelPlan.ingoingRoute) ? button.state('outgoing') : button.state('ingoing')
    }
  }

  #addStateButtons(states) {
    const button = L.easyButton({ states: states });
    button.addTo(this.map);

    return button;
  }

  #configMap() {
    if (isPresent(this.map)) return;

    this.routeLayers = {};
    this.routeButtons = [];
    this.map = L.map(this.mapContainerTarget, {
      zoomControl: false,
      scrollWheelZoom: true,
      tap: false,
      touchZoom: true,
      doubleClickZoom: false,
      contextmenu: true,
    });

    if (this.#isEditable()) {
      const addWaypointLabel = 'Adicionar ponto'
      const addWaypointMenuItem = this
        .map
        .contextmenu
        .insertItem({ text: addWaypointLabel, callback: this.#openAddWaypointModal.bind(this) });

      this.contextMenuItems[addWaypointLabel] = addWaypointMenuItem;
    }

    this.#setLayers();
    this.#setSidebar();

    this.map.setView(this.INIT_COORDINATES, this.INIT_ZOOM);

    L.control.zoom({ position: 'topright' }).addTo(this.map);
    this.map.options.minZoom = this.MIN_ZOOM;
    this.map.options.maxZoom = this.MAX_ZOOM;

    this.mapContainerTarget.height = getParent(this.mapContainerTarget).style.height

    this.commonPane = this.map.createPane('commonPane');
    this.activePane = this.map.createPane('activePane');
    this.activePane.style.zIndex = this.MAX_ZINDEX;
  }

  #setSidebar() {
    this.sidebar = L.control.sidebar({
      container: 'travel-plan-map-sidebar',
    });

    this.sidebar.addTo(this.map);
  }

  #setLayers() {
    // Due Google Maps API the property styles needs to receive an Array.
    const roadMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    const satMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'satellite',
      styles: []
    });

    const hybridMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'hybrid',
      styles: []
    });

    const transitMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    const trafficMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    transitMutant.addGoogleLayer('TransitLayer');
    trafficMutant.addGoogleLayer('TrafficLayer');

    const osm = L.tileLayer.provider('OpenStreetMap');
    const mapnik = L.tileLayer.provider('OpenStreetMap.Mapnik');

    const layers = {
      'Google Roadmap': roadMutant,
      'Google Roadmap Traffic': trafficMutant,
      'Google Roadmap Satellite': hybridMutant,
      'Google Transit': transitMutant,
      'Google Satellite': satMutant,
      'Mapnik': mapnik,
      'OpenStreetMaps': osm
    };

    roadMutant.addTo(this.map);

    this.control = L.control.layers(layers, {}, { position: 'bottomright' })
    this.control.addTo(this.map);
  }

  #garagesControlLayer() {
    API.api.fetchGarages(this.lineIdValue)
      .then(
        (response) => {
          const garages = response.data.garages;
          if (isBlank(garages)) return;

          const garageMarkers = garages
            .filter((garage) => [garage.address?.latitude, garage.address?.longitude].every(isPresent))
            .map((garage) => {
              return this.#createMarker(
                {
                  at: [garage.address.latitude, garage.address.longitude],
                  icon: L.ExtraMarkers.icon({
                    icon: 'fa-circle',
                    markerColor: '#8c2bca',
                    prefix: 'fa',
                    svg: true
                  }),
                  popupText: `<p>${garage.address.full_street_address}</p>`
                }
              );
            });
          if (isBlank(garageMarkers)) return;

          const layerGroup = L.layerGroup(garageMarkers);
          this.control.addOverlay(layerGroup, 'Mostrar garagems');
        }
      );
  }

  #subscriptionPopup(subscription) {
    let popup = "";

    popup += `<p>Nome: ${subscription.passenger.name}</p>`;
    popup += `<p>Id Passageiro: ${subscription.passenger.id} <a target='_blank' href='/admin/passengers/${subscription.passenger.id}'>(Acessar)</a></p>`;
    popup += `<p>Id Assinatura: ${subscription.id} <a target='_blank' href='/admin/subscriptions/${subscription.id}'>(Acessar)</a></p>`;

    if (subscription.status == 'pending') popup += "<p><b>Assinatura pendente</b></p>";

    popup += `<p>${subscription.passenger.full_address}</p>`;

    if (subscription.ingoing_executive_plan && subscription.ingoing_executive_plan.line_id == this.lineIdValue) {
      popup += `<p><b>IDA: </b>${subscription.ingoing_mode_of_travel}</p>`;

      if (isPresent(subscription.ingoing_origin_waypoint)) {
        popup += '<p>';
        popup += '<b>Embarque</b><br>';
        popup += `#${[subscription.ingoing_origin_waypoint.index, subscription.ingoing_origin_waypoint.address].join(' - ')}`;
        popup += "</p>";
        popup += "<p>";
        popup += "Distância até o embarque: ";
        popup += subscription.ingoing_origin_waypoint.distance;
        popup += "</p>";
      }

      if (isPresent(subscription.ingoing_destination_waypoint)) {
        popup += "<p>";
        popup += "<b>Desembarque</b><br>";
        popup += `#${[subscription.ingoing_destination_waypoint.index, subscription.ingoing_destination_waypoint.address].join(' - ')}`;
        popup += "</p>";
      }
    }

    if (isPresent(subscription.outgoing_executive_plan) && subscription.outgoing_executive_plan.line_id == this.lineIdValue) {
      popup += "<p>";
      popup += "<b>VOLTA: </b>";
      popup += subscription.outgoing_mode_of_travel;
      popup += "</p>";

      if (isPresent(subscription.outgoing_origin_waypoint)) {
        popup += "<p>";
        popup += "<b>Embarque</b><br>";
        popup += `#${[subscription.outgoing_origin_waypoint.index, subscription.outgoing_origin_waypoint.address].join(' - ')}`;
        popup += "</p>";
      }

      if (isPresent(subscription.outgoing_destination_waypoint)) {
        popup += "<p>";
        popup += "<b>Desembarque</b><br>";
        popup += `#${[subscription.outgoing_destination_waypoint.index, subscription.outgoing_destination_waypoint.address].join(' - ')}`;
        popup += "</p>";
        popup += "<p>";
        popup += "Distância do desembarque: " + subscription.outgoing_destination_waypoint.distance;
        popup += "</p>";
      }
    }

    popup += "</p>";

    return popup;
  }

  #subscriptionsControlLayer() {
    API.admin.fetchSubscriptions(this.lineIdValue)
      .then(
        (response) => {
          const subscriptions = response.data.subscriptions;
          if (isBlank(subscriptions)) return;

          const subscriptionsMarkers = subscriptions
            .filter((subscription) => isPresent(subscription.passenger?.address_ll))
            .map(
              (subscription) => {
                return this.#createMarker(
                  {
                    at: subscription.passenger.address_ll,
                    popupText: this.#subscriptionPopup(subscription),
                    icon: L.ExtraMarkers.icon({
                      icon: 'fa-user',
                      markerColor: (subscription.status == 'pending') ? '#ca842b' : '#1370b9',
                      shape: 'square',
                      prefix: 'fa',
                      svg: true
                    })
                  }
                );
              }
            );

          if (isBlank(subscriptionsMarkers)) return;

          const layerGroup = L.layerGroup(subscriptionsMarkers);
          this.control.addOverlay(layerGroup, 'Mostrar passageiros');
        }
      );
  }

  #waypointsControllerLayer({ route }) {
    const directionLabel = route.direction === 'ingoing' ? 'ida' : 'volta';
    if (isBlank(route.waypoints)) return;

    const waypointMarkers = this.#createWaypointMarkers({ route: route });
    const layerTitle = `Mostrar pontos ${directionLabel}`
    const layerGroup = L.layerGroup(waypointMarkers);

    this.controlLayers[this.#routeLayerIdentifier(route)] = { layerTitle: layerTitle, layerGroup: layerGroup };
    this.control.addOverlay(layerGroup, layerTitle);
  }

  #humanRouteDuration(route) {
    const duration = Number(route.duration);

    const hours = Math.floor(duration / 3600);
    const mins = Math.floor((duration % 3600) / 60);

    const humanDuration = mins > 0 ? `${hours} h ${mins} min` : `${hours} h`;

    return humanDuration;
  }

  #humanRouteDistance(route) {
    return `${Number(route.distance).toFixed(2).replace('.', ',')} km`;
  }

  #loadRouteInfoBox(route) {
    let humanDistance = null;
    let humanDuration = null;

    if (isPresent(route.distance)) {
      humanDistance = `<p class='Txt--smr'><span class="Txt--bold">Distância:</span> ${this.#humanRouteDistance(route)}</p>`;
    }

    if (isPresent(route.duration)) {
      humanDuration = `<p class='Txt--smr'><span class="Txt--bold">Duração:</span> ${this.#humanRouteDuration(route)}</p>`;
    }

    const prettyWaypointsInfo = route
      .waypoints
      .map((waypoint) => [waypoint.index, waypoint.officialSchedule, waypoint.fullAddress].join(' - '));


    const routeInfoBoxContent = [
      humanDistance,
      humanDuration,
      prettyWaypointsInfo.map((info) => `<p class='Txt--smr'>${info}</p>`).join("\n")
    ].filter(isPresent).join("\n");

    this.routeInfoBoxTarget.innerHTML = routeInfoBoxContent;
  }

  #centerMapAt(coords) {
    this.map.setView(coords, 20, { animation: true });
  }

  #isEditable() {
    if (!this.editableValue) return false;
    if (isPresent(this.travelPlan)) return this.#isFuture(this.travelPlan.effectiveFrom);

    return this.editableValue;
  }

  #isFuture(dateTimeInfo) {
    const dateTime = new Date(dateTimeInfo);
    const now = new Date();

    return dateTime > now;
  }

  #csrfToken() {
    return find("meta[name='csrf-token']").content;
  }

  #waypointUpdateAttributes(waypoint) {
    return {
      id: waypoint.id,
      uuid: waypoint.uuid,
      kindPoint: waypoint.kindPoint,
      cityId: waypoint.cityId,
      index: waypoint.index,
      streetLine: waypoint.streetLine,
      streetNumber: waypoint.streetNumber,
      neighbourhood: waypoint.neighbourhood,
      postalCode: waypoint.postalCode,
      reference: waypoint.reference,
      officialSchedule: waypoint.officialSchedule,
      latitude: waypoint.latitude,
      longitude: waypoint.longitude
    };
  }

  #routeUpdateAttributes(route) {
    const waypointsAttributes = sortBy(
      route.waypoints.map(this.#waypointUpdateAttributes.bind(this)),
      (waypointAttributes) => waypointAttributes.index
    );
    const attributes = { id: route.id, waypointsAttributes: waypointsAttributes };

    if (this.currentRoute?.id === route.id) {
      attributes['encodedPolyline'] = this.lastRoutingResponse.encodedPolyline || route.encodedPolyline;
      attributes['geojson'] = this.lastRoutingResponse.geojson || route.geojson;
      attributes['routingEngineResponse'] = this.lastRoutingResponse.engineResponse || route.engineResponse;
    }

    return attributes
  }

  #travelPlanUpdateAttributes() {
    if (this.currentRoute.direction === 'ingoing') {
      return {
        travelPlan: { ingoingRouteAttributes: this.#routeUpdateAttributes(this.currentRoute) }
      };
    } else {
      return {
        travelPlan: { outgoingRouteAttributes: this.#routeUpdateAttributes(this.currentRoute) }
      };
    }
  }

  updateTravelPlan(event) {
    event.preventDefault();
    this.#startLoading();
    const submitButton = event.target;
    const updatedWaypointUUID = this.updateWaypointFormModalUUIDTarget.value;
    const updateAttributes = this.#travelPlanUpdateAttributes();
    const waypointsAttributes = updateAttributes.travelPlan[`${this.currentRoute.direction}RouteAttributes`].waypointsAttributes;
    const updatedWaypointAttributes = waypointsAttributes.find((waypointAttributes) => waypointAttributes.uuid === updatedWaypointUUID);

    updatedWaypointAttributes.cityId = null;
    updatedWaypointAttributes.streetLine = null;
    updatedWaypointAttributes.streetNumber = null;
    updatedWaypointAttributes.neighbourhood = null;
    updatedWaypointAttributes.postalCode = null;

    updatedWaypointAttributes.index = parseInt(this.updateWaypointFormModalIndexTarget.value);
    updatedWaypointAttributes.latitude = parseFloat(this.updateWaypointFormModalLatitudeTarget.value);
    updatedWaypointAttributes.longitude = parseFloat(this.updateWaypointFormModalLongitudeTarget.value);
    updatedWaypointAttributes.officialSchedule = this.updateWaypointFormModalOfficialScheduleTarget.value;
    updatedWaypointAttributes.reference = this.updateWaypointFormModalReferenceTarget.value;
    updatedWaypointAttributes.customRadiusInMeters = parseFloat(this.updateWaypointFormModalCustomRadiusInMetersTarget.value);
    updatedWaypointAttributes.kindPoint = this.updateWaypointFormModalKindPointTarget.value;

    submitButton.disabled = true;

    this.#startLoading();
    API.admin.travelPlan.update(this.lineIdValue, this.travelPlanIdValue, updateAttributes, this.#csrfToken())
      .then((travelPlan) => {
        this.#loadTravelPlan(travelPlan, this.currentRoute.direction, false);
        $.modal.close();
        submitButton.disabled = false;
        this.#stopLoading();
      })
      .catch((error) => {
        console.error(error);
        alert(`Não foi possível atualizar o plano de viagem. ${error.message}`);
      });
  }

  updateAndReindexTravelPlan(event) {
    event.preventDefault();

    const submitButton = event.target;
    const updatedWaypointUUID = this.updateWaypointFormModalUUIDTarget.value;
    const updateAttributes = this.#travelPlanUpdateAttributes();
    const routeAttributes = updateAttributes.travelPlan[`${this.currentRoute.direction}RouteAttributes`];
    const waypointsAttributes = routeAttributes.waypointsAttributes;
    const updatedWaypointAttributes = waypointsAttributes.find((waypointAttributes) => waypointAttributes.uuid === updatedWaypointUUID);

    updatedWaypointAttributes.cityId = null;
    updatedWaypointAttributes.streetLine = null;
    updatedWaypointAttributes.streetNumber = null;
    updatedWaypointAttributes.neighbourhood = null;
    updatedWaypointAttributes.postalCode = null;

    updatedWaypointAttributes.index = parseInt(this.updateWaypointFormModalIndexTarget.value);
    updatedWaypointAttributes.latitude = parseFloat(this.updateWaypointFormModalLatitudeTarget.value);
    updatedWaypointAttributes.longitude = parseFloat(this.updateWaypointFormModalLongitudeTarget.value);
    updatedWaypointAttributes.officialSchedule = this.updateWaypointFormModalOfficialScheduleTarget.value;
    updatedWaypointAttributes.reference = this.updateWaypointFormModalReferenceTarget.value;
    updatedWaypointAttributes.kindPoint = this.updateWaypointFormModalKindPointTarget.value;

    this.#reindexWaypoints(waypointsAttributes);

    const coordinates = sortBy(waypointsAttributes, (waypoint) => waypoint.index)
      .map((waypoint) => [waypoint.latitude, waypoint.longitude]);
    const routingParams = {
      coordinates: coordinates,
      engine: this.currentRoute.routingEngine,
      vehicleCharacteristics: this.currentRoute.vehicleCharacteristics
    };

    submitButton.disabled = true;

    this.#startLoading();
    this.#buildRouteFrom(routingParams)
      .then((routingData) => {
        routeAttributes['encodedPolyline'] = routingData.encodedPolyline;
        routeAttributes['geojson'] = routingData.geojson;
        routeAttributes['routingEngineResponse'] = routingData.engineResponse;
      })
      .then(() => {
        API.admin.travelPlan.update(this.lineIdValue, this.travelPlanIdValue, updateAttributes, this.#csrfToken())
          .then((travelPlan) => {
            this.#loadTravelPlan(travelPlan, this.currentRoute.direction, false);
            $.modal.close();
            submitButton.disabled = false;
            this.#stopLoading();
          })
        .catch((error) => {
          $.modal.close();
          this.#stopLoading();
          console.error(error);
          alert(`Não foi possível atualizar o plano de viagem. ${error.message}`);
        });
      })
      .catch((error) => {
        console.error(error);
        alert(`Não foi possível traçar a rota no mapa. ${error.message}`);
      });
  }

  deleteWaypoint(event) {
    event.preventDefault();

    const submitButton = event.target;
    const deleteWaypointUUID = this.deleteWaypointFormModalUUIDTarget.value;
    const deletingWaypoint = this.currentRoute.waypoints.find((waypoint) => waypoint.uuid === deleteWaypointUUID);

    if (deletingWaypoint.hasPassengers) return this.#deleteWaypointsWithPassengersAlert();

    const updateAttributes = this.#travelPlanUpdateAttributes();
    const routeAttributes = updateAttributes.travelPlan[`${this.currentRoute.direction}RouteAttributes`];
    const waypointsAttributes = routeAttributes.waypointsAttributes;
    const deleteWaypointAttributes = waypointsAttributes.find((waypointAttributes) => waypointAttributes.uuid === deleteWaypointUUID);

    deleteWaypointAttributes._destroy = true

    this.#reindexWaypoints(waypointsAttributes);
    const keptWaypointAttributes = waypointsAttributes.filter((waypointAttributes) => isBlank(waypointAttributes._destroy));
    const coordinates = sortBy(keptWaypointAttributes, (waypoint) => waypoint.index)
      .map((waypoint) => [waypoint.latitude, waypoint.longitude]);
    const routingParams = {
      coordinates: coordinates,
      engine: this.currentRoute.routingEngine,
      vehicleCharacteristics: this.currentRoute.vehicleCharacteristics
    };

    submitButton.disabled = true;

    this.#startLoading();
    this.#buildRouteFrom(routingParams)
      .then((routingData) => {
        routeAttributes['encodedPolyline'] = routingData.encodedPolyline;
        routeAttributes['geojson'] = routingData.geojson;
        routeAttributes['routingEngineResponse'] = routingData.engineResponse;
      })
      .then(() => {
        API.admin.travelPlan.update(this.lineIdValue, this.travelPlanIdValue, updateAttributes, this.#csrfToken())
          .then((travelPlan) => {
            this.#loadTravelPlan(travelPlan, this.currentRoute.direction, false);
            $.modal.close();
            submitButton.disabled = false;
            this.#stopLoading();
          })
          .catch((error) => {
            $.modal.close();
            this.#stopLoading();
            console.error(error);
            alert(`Não foi possível atualizar o plano de viagem. ${error.message}`);
          });
      })
      .catch((error) => {
        console.error(error);
        alert(`Não foi possível traçar a rota no mapa. ${error.message}`);
      });
  }

  addWaypoint(event) {
    event.preventDefault();

    const submitButton = event.target;
    const newWaypointAttributes = {
      uuid: this.addWaypointFormModalUUIDTarget.value,
      index: parseInt(this.addWaypointFormModalIndexTarget.value),
      latitude: parseFloat(this.addWaypointFormModalLatitudeTarget.value),
      longitude: parseFloat(this.addWaypointFormModalLongitudeTarget.value),
      officialSchedule: this.addWaypointFormModalOfficialScheduleTarget.value,
      reference: this.addWaypointFormModalReferenceTarget.value,
      kindPoint: this.addWaypointFormModalKindPointTarget.value,
    };
    const updateAttributes = this.#travelPlanUpdateAttributes();
    const routeAttributes = updateAttributes.travelPlan[`${this.currentRoute.direction}RouteAttributes`];
    const waypointsAttributes = routeAttributes.waypointsAttributes;

    waypointsAttributes.push(newWaypointAttributes);

    this.#reindexWaypoints(waypointsAttributes);

    const coordinates = sortBy(waypointsAttributes, (waypoint) => waypoint.index)
      .map((waypoint) => [waypoint.latitude, waypoint.longitude]);

    submitButton.disabled = true;

    this.#startLoading();
    const routingParams = {
      coordinates: coordinates,
      engine: this.currentRoute.routingEngine,
      vehicleCharacteristics: this.currentRoute.vehicleCharacteristics
    };

    this.#buildRouteFrom(routingParams)
      .then((routingData) => {
        routeAttributes['encodedPolyline'] = routingData.encodedPolyline;
        routeAttributes['geojson'] = routingData.geojson;
        routeAttributes['routingEngineResponse'] = routingData.engineResponse;
      })
      .then(() => {
        API.admin.travelPlan.update(this.lineIdValue, this.travelPlanIdValue, updateAttributes, this.#csrfToken())
          .then((travelPlan) => {
            this.#loadTravelPlan(travelPlan, this.currentRoute.direction, false);
            $.modal.close();
            submitButton.disabled = false;
            this.#stopLoading();
          })
          .catch((error) => {
            $.modal.close();
            this.#stopLoading();
            console.error(error);
            alert(`Não foi possível atualizar o plano de viagem. ${error.message}`);
          });
      })
      .catch((error) => {
        console.error(error);
        alert(`Não foi possível traçar a rota no mapa. ${error.message}`);
      });
  }

  #clearSidebarForm() {
    this.addWaypointSidebarIndexTarget.value = ''
    this.addWaypointSideBarLatitudeTarget.value = ''
    this.addWaypointSidebarLongitudeTarget.value = ''
    this.addWaypointSidebarStreetLineTarget.value = ''
    this.addWaypointSidebarOfficialScheduleTarget.value = ''
    this.addWaypointSidebarReferenceTarget.value = ''
    this.addWaypointSidebarCustomRadiusInMetersTarget.value = this.addWaypointSidebarCustomRadiusInMetersTarget.dataset.defaultValue;
    this.addWaypointSidebarKindPointTarget.value
    const stopOption = find('option[value=stop]', this.addWaypointSidebarKindPointTarget);
    stopOption.selected = true;
  }

  addWaypointFromSidebar(event) {
    event.preventDefault();

    const submitButton = event.target;
    const newWaypointAttributes = {
      uuid: this.#generateUUID(),
      index: parseInt(this.addWaypointSidebarIndexTarget.value),
      latitude: parseFloat(this.addWaypointSideBarLatitudeTarget.value),
      longitude: parseFloat(this.addWaypointSidebarLongitudeTarget.value),
      streetLine: this.addWaypointSidebarStreetLineTarget.value,
      officialSchedule: this.addWaypointSidebarOfficialScheduleTarget.value,
      reference: this.addWaypointSidebarReferenceTarget.value,
      customRadiusInMeters: parseFloat(this.addWaypointSidebarCustomRadiusInMetersTarget.value),
      kindPoint: this.addWaypointSidebarKindPointTarget.value,
    };

    const updateAttributes = this.#travelPlanUpdateAttributes();
    const routeAttributes = updateAttributes.travelPlan[`${this.currentRoute.direction}RouteAttributes`];
    const waypointsAttributes = routeAttributes.waypointsAttributes;

    waypointsAttributes.push(newWaypointAttributes);

    this.#reindexWaypoints(waypointsAttributes);

    const coordinates = sortBy(waypointsAttributes, (waypoint) => waypoint.index)
      .map((waypoint) => [waypoint.latitude, waypoint.longitude]);
    const routingParams = {
      coordinates: coordinates,
      engine: this.currentRoute.routingEngine,
      vehicleCharacteristics: this.currentRoute.vehicleCharacteristics
    };

    submitButton.disabled = true;

    this.#startLoading();
    this.#buildRouteFrom(routingParams)
      .then((routingData) => {
        routeAttributes['encodedPolyline'] = routingData.encodedPolyline;
        routeAttributes['geojson'] = routingData.geojson;
        routeAttributes['routingEngineResponse'] = routingData.engineResponse;
      })
      .then(() => {
        API.admin.travelPlan.update(this.lineIdValue, this.travelPlanIdValue, updateAttributes, this.#csrfToken())
          .then((travelPlan) => {
            this.#loadTravelPlan(travelPlan, this.currentRoute.direction, false);
            $.modal.close();
            submitButton.disabled = false;
            this.#stopLoading();
            this.#clearSidebarForm();
          })
          .catch((error) => {
            this.#stopLoading();
            console.error(error);
            alert(`Não foi possível atualizar o plano de viagem. ${error.message}`);
          });
      })
      .catch((error) => {
        console.error(error);
        alert(`Não foi possível traçar a rota no mapa. ${error.message}`);
      });
  }

  #reindexWaypoints(waypoints) {
    const keepOrRemoveWaypoints = groupBy(waypoints, (waypoint) => isPresent(waypoint._destroy) ? 'destroy' : 'keep');
    const waypointsToKeep = keepOrRemoveWaypoints.keep || [];
    const waypointsToDestroy = keepOrRemoveWaypoints.destroy || [];

    waypointsToDestroy.forEach((waypoint) => waypoint.index = waypoint.index + 5555);

    this
      .#sortWaypointsByOfficialSchedule(waypointsToKeep)
      .forEach((waypoint, listPosition) => waypoint.index = listPosition + 1);
  }

  #minutesFromBeginningOfDay(schedule) {
    const [hours, minutes] = schedule.trim().split(':').map(Number);
    return hoursToMinutes(hours) + minutes;
  }

  #sortWaypointsByOfficialSchedule(waypoints) {
    const waypointsBeforeSorting = waypoints.map((waypoint) => waypoint);
    const minutesInADay = 1440;

    // Add Minutes attribute to waypoint to be the order reference.
    waypoints.forEach((waypoint) => waypoint.minutes = this.#minutesFromBeginningOfDay(waypoint.officialSchedule));

    // Sort waypoints based in its minutes.
    const waypointsInMinutesOrder = waypoints.sort((a, b) => a.minutes - b.minutes);

    // Detect the greatest interval between two hours in sequence
    let largestGapIndex = 0;
    let largestGap = 0;

    waypointsInMinutesOrder.forEach((currentWaypoint, currentIndex, sortedWaypoints) => {
      if (currentIndex > 0) {
        const previousWaypoint = sortedWaypoints[currentIndex - 1];
        const gapBetweenStops = currentWaypoint.minutes - previousWaypoint.minutes;

        if (gapBetweenStops > largestGap) {
          largestGap = gapBetweenStops;
          largestGapIndex = currentIndex;
        }
      }
    });

    // Considers the indervel between the last and the first (crossing day)
    const lastStop = waypointsInMinutesOrder[waypointsInMinutesOrder.length - 1];
    const firstStop = waypointsInMinutesOrder[0];
    const gapBetweenLastAndFirst = firstStop.minutes + minutesInADay - lastStop.minutes;

    if (gapBetweenLastAndFirst > largestGap) largestGapIndex = 0;

    // Reorganize the waypoints preservint its index
    const reorderedWaypoints = [
      ...waypointsInMinutesOrder.slice(largestGapIndex),
      ...waypointsInMinutesOrder.slice(0, largestGapIndex),
    ];

    // Remove the auxiliar sorting attribute minutes
    reorderedWaypoints.forEach((waypoint) => delete waypoint.minutes);

    const originalWaypointsUuidOrder = JSON.stringify(waypoints.map((waypoint) => waypoint.uuid));
    const reorderedWaypointsUuidOrder = JSON.stringify(reorderedWaypoints.map((waypoint) => waypoint.uuid));

    const changedOrder = !waypointsBeforeSorting
      .every((waypoint, position) => waypoint.uuid === reorderedWaypoints[position].uuid);

    if (changedOrder) this.#warn('Os pontos tiveram sua ordem alterada seguindo seus horários.');

    return reorderedWaypoints;
  }

  #notifierController() {
    const notificationControllerTag = find('#notifications-controller');

    return this
      .application
      .getControllerForElementAndIdentifier(notificationControllerTag, 'notifications');
  }

  #warn(message) {
    const notifier = this.#notifierController();
    notifier.titleValue = 'Atenção';
    notifier.messageValue = message;
    notifier.warn();
  }

  #error(message) {
    const notifier = this.#notifierController();
    notifier.titleValue = 'Erro';
    notifier.messageValue = message;
    notifier.error();
  }

  #success(message) {
    const notifier = this.#notifierController();
    notifier.titleValue = 'Ok';
    notifier.messageValue = message;
    notifier.success();
  }
});
